import React, { useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CheckboxInput, GetColor, Radio, SimpleTrigger } from 'venn-ui-kit';
import { usePortfolioMultiFiltering } from '../../../../../../../../venn-components/src/blocks/utils/hooks/use-portfolio-multi-filtering';
import { cn } from '@/lib/utils';
import { type BlockId } from 'venn-state';
import { PortfolioStrategyFilterType } from 'venn-components';
import styled from 'styled-components';

const topLevelOptions = [
  { value: PortfolioStrategyFilterType.DEFAULT, label: 'All Portfolio Constituents' },
  { value: PortfolioStrategyFilterType.ALL_STRATEGIES, label: 'Strategies Only' },
  { value: PortfolioStrategyFilterType.CUSTOM_SELECTION, label: 'Custom Selection' },
];

export const PortfolioMultiLevelDropdown = ({ blockId, isReadOnly }: { blockId: BlockId; isReadOnly: boolean }) => {
  const { filter, setFilter, getLevelsBasedOnFilter, levelSelectionOnly, isEmptyOrNoSubjects, allLevels } =
    usePortfolioMultiFiltering(blockId);

  const filterType = filter?.filterType ?? PortfolioStrategyFilterType.DEFAULT;
  const selectedLabel = useMemo(() => {
    if (isEmptyOrNoSubjects) {
      return 'None';
    }

    if (filter?.filterType === PortfolioStrategyFilterType.CUSTOM_SELECTION) {
      return (filter?.levels ?? []).length === 0 ? 'None' : `${(filter?.levels ?? []).length} selected`;
    }

    return topLevelOptions.findLast((o) => o.value === (filter?.filterType ?? 'DEFAULT'))?.label;
  }, [filter, isEmptyOrNoSubjects]);

  const currentLevels = getLevelsBasedOnFilter(filter?.filterType, allLevels, filter?.levels ?? []);
  const tooManySelected = levelSelectionOnly && (filter?.levels ?? []).length >= 2;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full" disabled={isEmptyOrNoSubjects} data-testid="strategy-levels-dropdown">
        <SimpleTrigger selection={selectedLabel} className="w-full" disabled={isEmptyOrNoSubjects} />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
          {!levelSelectionOnly && (
            <>
              {topLevelOptions.map((item) => (
                <DropdownMenuItem
                  className="h-[24.5px] pt-[4.5px] pb-[4.5px] pr-0 "
                  onSelect={(e) => {
                    if (isReadOnly) {
                      return;
                    }
                    const levels =
                      item.value === PortfolioStrategyFilterType.CUSTOM_SELECTION
                        ? []
                        : getLevelsBasedOnFilter(item.value, allLevels, filter?.levels ?? []);
                    setFilter({
                      filterType: item.value,
                      levels,
                      mergeRows: filter?.mergeRows,
                    });
                    e.preventDefault();
                  }}
                >
                  <Radio
                    className="pr-0 m-0"
                    inputId={`portfolioMultiLevelRadio${item.value}`}
                    value={item.value}
                    checked={(filter?.filterType ?? 'DEFAULT') === item.value}
                    name="top_options"
                    disabled={isReadOnly}
                  />
                  <label className="p-0 m-0 font-normal text-lg">{item.label}</label>
                  <br />
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          {levelSelectionOnly && (
            <div>
              <span className="px-[3px]  font-normal text-lg text-venn-grey-80">Select up to 2 levels</span>
              <DropdownMenuSeparator />
            </div>
          )}
          {allLevels
            .map((l) => ({
              value: l,
              label:
                l === 0 ? 'Top Level Strategies' : l === 'investments' ? 'Investments' : `Sub-strategies Level ${l}`,
            }))
            .map((level) => {
              const checked =
                !filterType ||
                filterType === PortfolioStrategyFilterType.DEFAULT ||
                (filterType === PortfolioStrategyFilterType.ALL_STRATEGIES && level.value !== 'investments') ||
                (filterType === PortfolioStrategyFilterType.CUSTOM_SELECTION &&
                  (filter?.levels ?? []).includes(level.value)) ||
                false;
              const checkboxDisabled = isReadOnly || (tooManySelected && !(filter?.levels ?? []).includes(level.value));

              return (
                <StyledDropdownMenuItem
                  onSelect={(e) => {
                    if (checkboxDisabled) {
                      return;
                    }

                    const prevLevels = getLevelsBasedOnFilter(filterType, allLevels, filter?.levels ?? []);
                    const newLevels = checked
                      ? prevLevels.filter((l) => l !== level.value)
                      : [...new Set([...prevLevels, level.value])].toSorted();

                    setFilter({
                      filterType: PortfolioStrategyFilterType.CUSTOM_SELECTION,
                      levels: newLevels,
                      mergeRows: filter?.mergeRows,
                    });

                    e.preventDefault();
                  }}
                >
                  <CheckboxInput checked={checked} disabled={checkboxDisabled} />
                  <label
                    className={cn(
                      'px-[3px]  font-normal text-lg',
                      levelSelectionOnly && currentLevels.length > 2 ? 'text-venn-error' : '',
                    )}
                  >
                    {level.label}
                  </label>
                  <Only
                    className="qa-checkbox-item-only ml-auto"
                    onClick={(e) => {
                      if (isReadOnly) {
                        return;
                      }

                      setFilter({
                        filterType: PortfolioStrategyFilterType.CUSTOM_SELECTION,
                        levels: [level.value],
                        mergeRows: filter?.mergeRows,
                      });
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    only
                  </Only>
                </StyledDropdownMenuItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

const Only = styled.div`
  opacity: 0;
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${GetColor.Primary.Dark};
  font-size: 11px;
  cursor: pointer;
  line-height: 24px;
  width: 33px;
  padding: 0;
  margin-right: 10px;
  display: inline-flex;
  align-items: center;
`;

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
  display: flex;
  align-items: center;
  width: 100%;

  &:hover > ${Only} {
    opacity: 1;
  }
`;
